import React, { createContext, useContext, useEffect, useMemo, useState, useCallback, ReactNode } from 'react'
import { Session } from '@ory/client'
import { sdk } from '../services/sdk'
import { parseJwt } from '../utils/jwt'
import { getSIdentityUrl } from '../services/http-common'

interface SistemivMetadataPublic {
  azure_ad_b2c_id: string
}

interface AuthContextType {
  isLoggedIn: boolean
  session: Session | null
  logoutUrl: string
  loading: boolean
  userId: string | undefined
  fetchSession: () => Promise<void>
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [session, setSession] = useState<Session | null>(null)
  const [loading, setLoading] = useState(true)
  const [init, setInit] = useState(false)
  const [logoutUrl, setLogoutUrl] = useState<string>('')

  const userId = useMemo(() => {
    const oid = (session?.identity?.metadata_public as SistemivMetadataPublic)?.azure_ad_b2c_id
    return oid || session?.identity?.id
  }, [session])

  const createLogoutFlow = useCallback(async () => {
    try {
      const { data } = await sdk.createBrowserLogoutFlow({
        returnTo: window.location.origin,
      })
      setLogoutUrl(data.logout_url)
    } catch (error: any) {
      //can't create logout flow
    }
  }, [])

  const fetchSession = useCallback(async () => {
    try {
      setLoading(true)
      const { data: session } = await sdk.toSession({
        tokenizeAs: 'sistemiv-dev',
      })
      localStorage.setItem('access_token', session.tokenized || '')
      setSession(session)
      if (!logoutUrl) {
        createLogoutFlow()
      }
    } catch (error: any) {
      if (error.response?.status === 401 || error.response?.status === 403) {
        window.location.href = `${getSIdentityUrl()}/login?return_to=${window.location.origin}?env=${
          process.env.REACT_APP_STAGE
        }`
      }
    } finally {
      setLoading(false)
    }
  }, [createLogoutFlow, logoutUrl])

  useEffect(() => {
    if (session) {
      const jwtExpiresAt = parseJwt(session?.tokenized || '')?.exp
      const expiresAt = new Date(jwtExpiresAt * 1000)
      const now = new Date()
      if (expiresAt < now) {
        fetchSession()
      }
    } else {
      if (!init) {
        setInit(true)
        fetchSession()
      }
    }
  }, [createLogoutFlow, logoutUrl, session, fetchSession, init])

  const value = {
    isLoggedIn: !!session,
    session,
    logoutUrl,
    loading,
    userId,
    fetchSession,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}
